// Reward API service.
//
// Talks to the Laravel Admin Rewards endpoint so the User App's reward
// screens (RewardCards / ScratchCard) are driven by Admin-configured data
// instead of the hardcoded REWARDS list. Requests go through apiClient so
// they carry the Dealzta bearer token when one is stored.

import { apiFetch, fetchJson } from './apiClient';

/**
 * Fetch the full reward list from the Laravel Admin API
 * (GET /admin/rewards). Resolves to the raw array of reward objects on
 * success (may be empty), throws on a genuine API/network failure.
 */
export async function getRewards() {
  const data = await fetchJson('/admin/rewards');
  return Array.isArray(data) ? data : [];
}

/**
 * Fetch rewards and reduce them to what the User App shows: only active
 * rewards, in the order the API returned them (no invented ordering field
 * is applied here).
 */
export async function getActiveRewards() {
  const rewards = await getRewards();
  return rewards.filter((reward) => reward.active === true);
}

/**
 * Map a raw Laravel reward object onto the fields the existing reward UI
 * (RewardCards / ScratchCard) expects, without fabricating anything the
 * API didn't provide:
 *   title       -> discount   (card's primary/large reward text)
 *   description -> description
 *   deal_code   -> code
 * Every original Laravel field (id, journey_id, wallet_credit, active,
 * start_date, expiry_date, created_at, updated_at, ...) is preserved on
 * the mapped object for future steps.
 */
export function mapRewardForUI(reward) {
  return {
    ...reward,
    discount: reward.title,
    description: reward.description ?? null,
    code: reward.deal_code ?? null,
  };
}

/**
 * Fetch the single reward belonging to the authenticated user's own
 * server-verified completed journey
 * (GET /user-missions/journey-reward — Step 5G). Identity and completion
 * are both derived server-side from the Dealzta bearer token; no journey id
 * or user id is ever sent. Resolves to `null` (not an error) when the user
 * has no completed journey yet, or their completed journey has no
 * active/valid reward configured — both expected, non-error states. Throws
 * only on a genuine network/server failure.
 */
export async function getCompletedJourneyReward() {
  const response = await apiFetch('/user-missions/journey-reward');
  const body = await response.json();

  if (response.status === 404) {
    return null;
  }

  if (!response.ok || !body.success) {
    throw new Error(body.message || 'Unable to fetch the completed journey reward.');
  }

  return body.data;
}

/**
 * Reveal (idempotently persist) the reward for the authenticated user's own
 * server-verified completed journey
 * (POST /user-missions/reveal-reward — Step 5G). Sends no body — the
 * backend alone resolves the acting user, the completed journey, and the
 * reward; the frontend never supplies user_id or reward_id. Safe to call
 * more than once for the same reward: the backend returns the existing
 * reveal instead of creating a duplicate. Resolves to
 * { reward, reveal }; throws on failure (including "no completed journey"
 * / "no reward configured", which are genuine failures for a reveal
 * attempt, unlike for getCompletedJourneyReward above).
 */
export async function revealCompletedJourneyReward() {
  return fetchJson('/user-missions/reveal-reward', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}
